import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { InfoContext } from './AM_BackGroundOP';
import CastingHistory from '../pages/Projects/CastingHistory';

const portalRoot = document.getElementById('portal-root');

const FloatingWindow = ({ children, windowTitle }) => {
    const ctx = useContext(InfoContext);
    const elRef = useRef(document.createElement('div'));
    const windowRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const isDragging = useRef(false);
    const position = useRef({ x: 100, y: 100 });
    const size = useRef({ width: 600, height: 480 });
    const dragStart = useRef({ x: 0, y: 0 });
    const headerRef = useRef(null);
    const closeRef = useRef(null);
    const resizeCornerRef = useRef(null);
    const resizeRightRef = useRef(null);
    const resizeBottomRef = useRef(null);
    const resizeDirection = useRef(null);
    const margin = 20;
    
    const savePosition = () => {
        sessionStorage.setItem('floatingWindowPosition', JSON.stringify(position.current));
        sessionStorage.setItem('floatingWindowSize', JSON.stringify(size.current));
    }
    
    const setPosition = (newPosition, newSize) => {
        
        const changePosition = newPosition;
        const changeSize = newSize;
        
        if(changePosition) {
            const currentSize = newSize ?? size.current;
            if(currentSize.width > window.innerWidth - (margin * 2)) currentSize.width = window.innerWidth - (margin * 2);
            if(currentSize.height > window.innerHeight - (margin * 2)) currentSize.height = window.innerHeight - (margin * 2);
            position.current = {
                x: newPosition.x < margin ? margin : (newPosition.x + currentSize.width) > (window.innerWidth - margin) ? (window.innerWidth - margin) - currentSize.width : newPosition.x,
                y: newPosition.y < margin ? margin : (newPosition.y + currentSize.height) > (window.innerHeight - margin) ? (window.innerHeight - margin) - currentSize.height : newPosition.y,
            }
        } 
        
        if(changeSize) {
            if((window.innerWidth - (margin * 2)) < newSize.width) newSize.width = window.innerWidth - (margin * 2);
            if((window.innerHeight - (margin * 2)) < newSize.heigh) newSize.height = window.innerHeight - (margin * 2);
            size.current = {
                width: newSize.width < 300 ? 300 : (position.current.x + newSize.width) > (window.innerWidth - margin) ? (window.innerWidth - margin) - position.current.x : newSize.width,
                height: newSize.height < 150 ? 150 : (position.current.y + newSize.height) > (window.innerHeight - margin) ? (window.innerHeight - margin) - position.current.y : newSize.height,
            }
        }

        windowRef.current.style.top = position.current.y + 'px';
        windowRef.current.style.left = position.current.x + 'px';
        windowRef.current.style.width = size.current.width + 'px';
        windowRef.current.style.height = size.current.height + 'px';
    }

    const handleMouseMove = useCallback((e) => {
        const isTouch = e.type === 'touchmove';
        if(isTouch) {
            if(e.touches?.length === 0) return;
            e = e.touches[0]; 
        }
        if (isDragging.current) {
            const newX = e.clientX - dragStart.current.x;
            const newY = e.clientY - dragStart.current.y;    
            setPosition({x: newX, y: newY});
        }
    }, []);
    
    const handleResize = useCallback((e) => {
        const isTouch = e.type === 'touchmove';
        if(isTouch) {
            if(e.touches?.length === 0) return;
            e = e.touches[0]; 
        }
        const rect = windowRef.current.getBoundingClientRect();
        const newWidth = resizeDirection.current === 'height' ? rect.width : e.clientX - rect.left;
        const newHeight = resizeDirection.current === 'width' ? rect.height : e.clientY - rect.top;
        setPosition(null, {width: Math.max(newWidth, 200), height: Math.max(newHeight, 150)});
    }, []);
    
    const stopDragging = useCallback(() => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', stopDragging);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', stopDragging);
        savePosition();
    }, [handleMouseMove]);
    
    const startDragging = useCallback((e) => {
        if(e.target === closeRef.current) return;
        e.preventDefault();
        const isTouch = e.type === 'touchstart';
        if(isTouch) {
            if(e.touches?.length === 0) return;
            e = e.touches[0]; 
        }
        isDragging.current = true;
        dragStart.current = {
            x: e.clientX - position.current.x,
            y: e.clientY - position.current.y,
        };
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const stopEvent = isTouch ? 'touchend' : 'mouseup';
        document.addEventListener(moveEvent, handleMouseMove, { passive: false });
        document.addEventListener(stopEvent, stopDragging);
    }, [handleMouseMove, stopDragging]);
    
    const stopResize = useCallback(() => {
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', stopResize);
        document.removeEventListener('touchmove', handleResize);
        document.removeEventListener('touchend', stopResize);
        savePosition();
    }, [handleResize]);

    const startResize = useCallback((e) => {
        const isTouch = e.type === 'touchstart';
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const stopEvent = isTouch ? 'touchend' : 'mouseup';
        resizeDirection.current = e.target === resizeRightRef.current ? 'width' : e.target === resizeBottomRef.current ? 'height' : null;   
        document.addEventListener(moveEvent, handleResize, { passive: false });
        document.addEventListener(stopEvent, stopResize);
        e.preventDefault();
    }, [handleResize, stopResize]);


    const closeWithFade = (e) => {
        savePosition();
        setIsVisible(false);
        setTimeout(ctx.portalOpen.current(false), 300);
    };
    
    const mainStyle = {
        position: 'fixed',
        top: position.current.y,
        left: position.current.x,
        width: size.current.width,
        height: size.current.height,
        backgroundColor: 'white',
        boxShadow: '0 0 15px rgba(0,0,0,0.3)',
        zIndex: 1000,
        borderRadius: '8px',
        overflow: 'hidden',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        userSelect: isDragging.current ? 'none' : 'auto',
    }
    
    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px',
        backgroundColor: '#1A3763',
        color: 'white',
        cursor: 'move',
        
    }
    
    const headerSpan = {
        overflow: 'hidden', 
        textWrapMode: 'nowrap',
        textOverflow: 'ellipsis',
        paddingLeft: '4px',
    }
    
    const closeButtonStyle = {
        float: 'right',
        background: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '16px',
    }
    
    const contentStyle = {
        flex: 1, 
        overflow: 'auto', 
        margin: '8px 0',
        padding: '0 8px',
    }
    
    const resizeStyle = {
        width: '8px',
        height: '8px',
        position: 'absolute',
        right: 0,
        bottom: 0,
        cursor: 'nwse-resize',
        backgroundColor: 'transparent',
    }
    
    useEffect(() => {
        if(!window.matchMedia('(pointer: coarse)').matches) return;
        
        const header = headerRef.current;
        const resizeRight = resizeRightRef.current;
        const resizeBottom = resizeBottomRef.current;
        const resizeCorner = resizeCornerRef.current;
        header.addEventListener('touchstart', startDragging, { passive: false });
        resizeRight.addEventListener('touchstart', startResize, { passive: false });
        resizeBottom.addEventListener('touchstart', startResize, { passive: false });
        resizeCorner.addEventListener('touchstart', startResize, { passive: false });
        
        return () => {
            header.removeEventListener('touchstart', startDragging);
            resizeRight.removeEventListener('touchstart', startResize);
            resizeBottom.removeEventListener('touchstart', startResize);
            resizeCorner.removeEventListener('touchstart', startResize);
        }  
    }, [startDragging, startResize])
    
    useEffect(() => {    
        const el = elRef.current;
        portalRoot.appendChild(el);
        setTimeout(() => setIsVisible(true), 10);
        
        let p = sessionStorage.getItem('floatingWindowPosition');
        if(p) {   
            try {
                p = JSON.parse(p);
                if(p.hasOwnProperty('x')) position.current.x = p.x;
                if(p.hasOwnProperty('y')) position.current.y = p.y;
            } catch (error) {}             
        } 

        let s = sessionStorage.getItem('floatingWindowSize');
        if(s) {   
            try {
                s = JSON.parse(s);
                if(s.hasOwnProperty('width')) size.current.width = s.width;
                if(s.hasOwnProperty('height')) size.current.height = s.height;
            } catch (error) {}             
        } 
        
        const setPositionOnResize = () => {        
              setPosition(position.current, size.current);
              savePosition(); 
        }
        
        setPositionOnResize();  
        window.addEventListener('resize', setPositionOnResize);
        
        return () => {
            window.removeEventListener('resize', setPositionOnResize);
            portalRoot.removeChild(el);
        };  
    }, []);

    return createPortal(
        <div key='main' style={mainStyle} ref={windowRef}>
            {/* Header */}
            <div ref={headerRef}
                key='header' 
                onMouseDown={startDragging} 
                style={headerStyle}>
                <span style={headerSpan}>{windowTitle}</span>
                <button ref={closeRef} onClick={closeWithFade} style={closeButtonStyle}>✕</button>
            </div>
            {/* Content */}
            <div key='content' style={contentStyle}>{children}</div>
            {/* Resize corner */}
            <div key='resizeCorner' 
                ref={resizeCornerRef}
                onMouseDown={startResize} 
                style={resizeStyle}/>
            {/* Resize right */}
            <div ref={resizeRightRef} 
                key='resizeRight' 
                onMouseDown={startResize} 
                style={{...resizeStyle, height: 'calc(100% - 8px)', cursor: 'w-resize', top: 0}}/>
            {/* Resize bottom */}
            <div ref={resizeBottomRef} 
                key='resizeBottom' 
                onMouseDown={startResize} 
                style={{...resizeStyle, width: 'calc(100% - 8px)', cursor: 's-resize', left: 0}}/>
        </div>,
        elRef.current
    );
};

export const FloatingWindowLauncher = () => {
    const ctx = useContext(InfoContext);
    const [open, setOpen] = useState(false); 
    const [windowTitle, setWindowTitle] = useState('Floating Window');
    const [childrenData, setChildrenData] = useState(null);
    const openWindow = (val, title, data) => {
        setOpen(val);
        setWindowTitle(title);
        setChildrenData(data);
    }
     
    useEffect(() => {
        ctx.portalOpen.current = openWindow;
    }, [ctx])
  
    return open 
        ? 
        <FloatingWindow windowTitle={windowTitle}>
            <CastingHistory data={childrenData}/>
        </FloatingWindow> 
        : 
        null;
};

